import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import queryString from 'query-string'
import { useState } from 'react'
import styled, { css } from 'styled-components'

import { InlineSize } from '../../components/Base/InlineSize'
import { Box } from '../../components/Box/Box'
import { Button } from '../../components/Button/Button'
import { Notes } from '../../components/Notes/Notes'
import { PageTitle } from '../../components/PageTitle/PageTitle'
import { isConsole } from '../../config/config'
import { formattedName } from '../../helpers/formatters'
import { Handler, HandlerOf } from '../../helpers/typeHelper'
import { ChildrenDocument, ChildrenDocumentAttachment, ChildrenDocumentMissing } from '../../models/document'
import { Job } from '../../models/job'
import { ProductSubscriptionBundleName } from '../../models/productSubscription'
import { Team } from '../../models/team'
import { Truck } from '../../models/truck'
import { User } from '../../models/user'
import { ProfileHeader } from './Profile/ProfileHeader'
import { ProfileSection } from './Profile/ProfileSection'
import { ProfileSubNav } from './Profile/ProfileSubNav'
import { ProfileDocuments } from './ProfileDocuments/ProfileDocuments'
import { ProfileEmployment } from './ProfileEmployment/ProfileEmployment'
import { ProfileIssues } from './ProfileIssues/ProfileIssues'
import { ProfileJobs } from './ProfileJobs/ProfileJobs'
import { ProfileOnboarding } from './ProfileOnboarding/ProfileOnboarding'
import { OverviewSection } from './ProfileOverview/OverviewSection'
import { ProfileSettings } from './ProfileSettings/ProfileSettings'

interface ProfilePageProps {
  user: User
  assignedTruck?: Truck
  team: Team | null
  teamOpener?: (sidebarOpener: React.ReactNode) => React.ReactNode
  onUserRoleChange?: HandlerOf<User>
  onProgramEnrollment?: HandlerOf<User>
  onTeamChange?: Handler
  onJobOpen: HandlerOf<Job>
  jobUrlConstructor?: (job: Job) => string | undefined
  onDocumentOpen?: (
    document: ChildrenDocument | ChildrenDocumentMissing,
    attachments?: ChildrenDocumentAttachment[]
  ) => React.ReactNode
  renderDeactivateUserButton?: () => React.ReactNode
  renderOnboarding?: (setActiveTab: (tab: ProfilePageTab) => void) => React.ReactNode
  renderEmployment?: (setActiveTab: (tab: ProfilePageTab) => void) => React.ReactNode
  renderDriverTransition?: () => React.ReactNode
  renderDocumentAction?: (
    document: ChildrenDocument | ChildrenDocumentMissing,
    attachments?: ChildrenDocumentAttachment[],
    refetchDocuments?: () => Promise<any>
  ) => React.ReactNode
  tabs: ProfilePageTab[]
  initialTab?: ProfilePageTab
  isInsideSidebar?: boolean
  hasJobListRedesignFlag?: boolean
}

export enum ProfilePageTab {
  OVERVIEW = 'overview',
  PROFILE = 'profile',
  JOBS = 'jobs',
  DOCUMENTS = 'documents',
  ONBOARDING = 'onboarding',
  EMPLOYMENT = 'employment',
  SETTINGS = 'settings',
  ISSUES = 'issues',
}

const ProfilePageTabLabels = {
  [ProfilePageTab.OVERVIEW]: 'Overview', // CONSOLE SIDEBAR ONLY
  [ProfilePageTab.PROFILE]: 'Profile',
  [ProfilePageTab.JOBS]: 'Jobs',
  [ProfilePageTab.DOCUMENTS]: 'Documents',
  [ProfilePageTab.ONBOARDING]: 'Onboarding Tasklist',
  [ProfilePageTab.EMPLOYMENT]: 'Employment History',
  [ProfilePageTab.SETTINGS]: 'Settings',
  [ProfilePageTab.ISSUES]: 'Issues',
}

const onboardingTabs = [ProfilePageTab.ONBOARDING, ProfilePageTab.EMPLOYMENT]

function getDefaultTab() {
  const tab = window.location.hash.replace('#', '')?.split('?')[0]
  if (Object.values(ProfilePageTab).includes(tab as ProfilePageTab)) {
    return tab as ProfilePageTab
  }

  if (queryString.parse(window.location.search).envelopeId) {
    window.location.hash = ProfilePageTab.ONBOARDING
    return ProfilePageTab.ONBOARDING
  }

  return null
}

export function ProfilePage({
  user,
  assignedTruck,
  team,
  teamOpener,
  tabs,
  initialTab,
  onUserRoleChange,
  onProgramEnrollment,
  onTeamChange,
  onJobOpen,
  jobUrlConstructor,
  onDocumentOpen,
  renderDeactivateUserButton,
  renderOnboarding,
  renderEmployment,
  renderDriverTransition,
  renderDocumentAction,
  isInsideSidebar,
  hasJobListRedesignFlag,
}: ProfilePageProps) {
  const [activeTab, setActiveTab] = useState(getDefaultTab() || initialTab || tabs[0])

  function handleSetActiveTab(activeTab: ProfilePageTab) {
    window.location.hash = activeTab
    setActiveTab(activeTab)
  }

  const subNavItems = tabs.map((tab) => ({
    label: ProfilePageTabLabels[tab],
    onClick: () => handleSetActiveTab(tab),
    isActive: activeTab === tab,
  }))

  const subNavOnboardingItems =
    isConsole() && !isInsideSidebar && team?.productSubscriptionName === ProductSubscriptionBundleName.PREMIUM
      ? onboardingTabs.map((tab) => ({
          label: ProfilePageTabLabels[tab],
          onClick: () => handleSetActiveTab(tab),
          isActive: activeTab === tab,
        }))
      : []

  const isWideContent = [
    ...(isInsideSidebar ? [] : [ProfilePageTab.JOBS]),
    ProfilePageTab.DOCUMENTS,
    ...onboardingTabs,
    ProfilePageTab.ISSUES,
  ].includes(activeTab)
  const isOnboardingTabActive = onboardingTabs.includes(activeTab)

  return (
    <Notes
      resetExpandedStateTrigger={!isWideContent}
      recordId={user.id}
      recordType="users"
      recordName={formattedName(user)}
      expandByDefault={false}
      isTimeline={isConsole()}
      isInsideSidebar={isInsideSidebar}
    >
      {(renderOpenNotesCard) => (
        <Wrap $isWide={isWideContent} $isInsideSidebar={isInsideSidebar}>
          <PageTitle title={`${user.firstName} ${user.lastName}`} />
          <div>
            {isOnboardingTabActive ? (
              <Button
                variant="text"
                size="small"
                icon={<ArrowBackIcon fontSize="small" />}
                onClick={() => handleSetActiveTab(ProfilePageTab.PROFILE)}
              >
                {user.firstName} {user.lastName}
              </Button>
            ) : (
              <ProfileHeader
                user={user}
                assignedTruck={assignedTruck}
                team={team}
                teamOpener={teamOpener}
                onUserRoleChange={onUserRoleChange}
                onProgramEnrollment={onProgramEnrollment}
                onTeamChange={onTeamChange}
                renderDriverTransition={renderDriverTransition}
                renderOpenNotesCard={renderOpenNotesCard}
                isInsideSidebar={isInsideSidebar}
              />
            )}
            <Box mt={3} />
            <ProfileSubNav
              items={subNavItems}
              subNavOnboardingItems={subNavOnboardingItems}
              isOnboardingTabActive={isOnboardingTabActive}
            />
            <Box mt={4} />
            <div style={isInsideSidebar ? { marginLeft: -40, marginRight: -40 } : undefined}>
              {activeTab === ProfilePageTab.PROFILE && (
                <ProfileSection user={user} renderDeactivateUserButton={renderDeactivateUserButton} />
              )}
              {activeTab === ProfilePageTab.OVERVIEW && (
                <OverviewSection user={user} isInsideSidebar={isInsideSidebar} />
              )}

              {activeTab === ProfilePageTab.JOBS && (
                <ProfileJobs
                  user={user}
                  handleJobOpen={onJobOpen}
                  jobUrlConstructor={jobUrlConstructor}
                  isInsideSidebar={isInsideSidebar}
                  hasJobListRedesignFlag={hasJobListRedesignFlag}
                />
              )}
              {activeTab === ProfilePageTab.DOCUMENTS && (
                <ProfileDocuments
                  user={user}
                  onDocumentOpen={onDocumentOpen}
                  renderDocumentAction={renderDocumentAction}
                />
              )}
              {activeTab === ProfilePageTab.ONBOARDING && (
                <ProfileOnboarding renderOnboarding={() => renderOnboarding?.(setActiveTab)} />
              )}
              {activeTab === ProfilePageTab.EMPLOYMENT && (
                <ProfileEmployment renderEmployment={() => renderEmployment?.(setActiveTab)} />
              )}
              {activeTab === ProfilePageTab.SETTINGS && <ProfileSettings user={user} team={team} />}
              {activeTab === ProfilePageTab.ISSUES && <ProfileIssues userId={user.id} />}
            </div>
          </div>
        </Wrap>
      )}
    </Notes>
  )
}

const RESPONSIVE_WRAP_WIDTH = 762
const RESPONSIVE_WRAP_WIDE_WIDTH = 1180

const Wrap = styled(InlineSize)<{
  $isWide?: boolean
  $isInsideSidebar?: boolean
}>`
  width: 100%;
  max-width: ${RESPONSIVE_WRAP_WIDTH}px;

  ${isConsole() &&
  css`
    min-width: ${RESPONSIVE_WRAP_WIDTH}px;
  `}

  ${(p) =>
    p.$isWide &&
    isConsole() &&
    css`
      max-width: none;
      width: ${RESPONSIVE_WRAP_WIDE_WIDTH}px;
    `}

  ${(p) =>
    p.$isInsideSidebar &&
    isConsole() &&
    css`
      max-width: none;
      min-width: 0;
    `}
`
